import type { SaveKind, SaveSlot } from '@replay/shared';
import { api, ApiError } from '../lib/api.ts';
import { deviceName } from '../lib/format.ts';

/**
 * Local-first save synchronisation.
 *
 * Every save is written to this device (IndexedDB) before any network call, so
 * a crash or disconnection never loses progress. The cloud copy is then
 * updated with optimistic concurrency (baseVersion); the server keeps a
 * version history, so even a forced overwrite never destroys older data.
 * Saves are keyed by engine + core: states from other emulators are never mixed.
 */

export interface LocalSave {
  key: string;
  userId: string;
  gameId: string;
  kind: SaveKind;
  slot: number;
  engine: string;
  core: string;
  coreVersion: string;
  data: Uint8Array;
  sha256: string;
  /** Server head version this local copy is based on (0 = never synced). */
  baseVersion: number;
  dirty: boolean;
  updatedAt: number;
  screenshot?: Blob | null;
  /** Set when the cloud moved on while this copy was offline; needs a user decision. */
  conflict?: { headVersion: number; sha256: string; updatedAt: string; deviceName: string | null } | null;
  /** Game title, for listing pending saves without network. */
  title?: string;
}

export type SyncStatus =
  | { state: 'idle' }
  | { state: 'saving' }
  | { state: 'synced'; at: number }
  | { state: 'pending'; reason: string }
  | { state: 'conflict'; local: LocalSave; server: SaveSlot }
  | { state: 'error'; message: string };

const DB = 'replay-saves';
let dbp: Promise<IDBDatabase> | null = null;
function db() {
  dbp ??= new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      const d = r.result;
      d.createObjectStore('saves', { keyPath: 'key' });
      d.createObjectStore('backups', { autoIncrement: true });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
  return dbp;
}
async function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function sha256Hex(data: Uint8Array) {
  const d = await crypto.subtle.digest('SHA-256', data as BufferSource);
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('');
}

export const saveKey = (userId: string, gameId: string, kind: SaveKind, slot: number, engine: string, core: string) =>
  [userId, gameId, kind, slot, engine, core].join('|');

export const getLocal = (key: string) => tx<LocalSave | undefined>('saves', 'readonly', (s) => s.get(key));
const putLocal = (v: LocalSave) => tx('saves', 'readwrite', (s) => s.put(v));
const deleteLocal = (key: string) => tx('saves', 'readwrite', (s) => s.delete(key));
const backup = (v: LocalSave) => tx('backups', 'readwrite', (s) => s.add({ ...v, backedUpAt: Date.now() }));
export const listLocal = () => tx<LocalSave[]>('saves', 'readonly', (s) => s.getAll());

async function download(slotId: string) {
  const res = await api<Response>(`/api/saves/${slotId}/data`, { raw: true });
  const data = new Uint8Array(await res.arrayBuffer());
  return { data, version: Number(res.headers.get('x-save-version')), sha256: res.headers.get('x-save-sha256') ?? (await sha256Hex(data)) };
}

async function upload(v: LocalSave, force = false): Promise<SaveSlot> {
  const slot = await api<SaveSlot>(`/api/games/${v.gameId}/saves/${v.kind}/${v.slot}`, {
    method: 'PUT',
    body: v.data,
    query: { engine: v.engine, core: v.core, coreVersion: v.coreVersion, baseVersion: v.baseVersion, force: force ? 1 : 0, device: deviceName() },
  });
  if (v.screenshot) {
    await api(`/api/saves/${slot.id}/versions/${slot.headVersion}/screenshot`, { method: 'PUT', body: v.screenshot }).catch(() => {});
  }
  return slot;
}

export class SaveSync {
  status: SyncStatus = { state: 'idle' };
  private listeners = new Set<(s: SyncStatus) => void>();
  private serverSlots: SaveSlot[] | null = null;
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  private chain: Promise<unknown> = Promise.resolve();

  /** Game title, stored with local saves so they can be listed offline. */
  title?: string;

  constructor(
    readonly userId: string,
    readonly gameId: string,
    readonly engine: string,
    readonly core: string,
    public coreVersion = '',
  ) {
    this.retryTimer = setInterval(() => void this.retryPending(), 30_000);
    window.addEventListener('online', this.onOnline);
  }

  dispose() {
    if (this.retryTimer) clearInterval(this.retryTimer);
    window.removeEventListener('online', this.onOnline);
  }

  private onOnline = () => void this.retryPending();

  onStatus(fn: (s: SyncStatus) => void) {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }
  private set(s: SyncStatus) {
    this.status = s;
    for (const l of this.listeners) l(s);
  }

  key(kind: SaveKind, slot: number) {
    return saveKey(this.userId, this.gameId, kind, slot, this.engine, this.core);
  }

  private server(kind: SaveKind, slot: number) {
    return this.serverSlots?.find((s) => s.kind === kind && s.slot === slot && s.engine === this.engine && s.core === this.core) ?? null;
  }

  /** Fetch cloud slots. Returns false when offline (local copies will be used). */
  async refresh() {
    try {
      this.serverSlots = await api<SaveSlot[]>(`/api/games/${this.gameId}/saves`);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.retryable) return false;
      throw e;
    }
  }

  /**
   * Decide which copy of a save to load at game start.
   * Returns the bytes to load, or a conflict the user must resolve.
   */
  async resolve(kind: SaveKind, slot: number): Promise<{ data: Uint8Array | null; conflict?: { local: LocalSave; server: SaveSlot } }> {
    const local = await getLocal(this.key(kind, slot));
    const online = this.serverSlots !== null;
    const server = this.server(kind, slot);
    if (!online) return { data: local?.data ?? null };
    if (!server) {
      // Synced before and gone from the cloud now: it was deleted there. Keep a backup, start clean.
      if (local && !local.dirty && local.baseVersion > 0) {
        await backup(local);
        await deleteLocal(local.key);
        return { data: null };
      }
      if (local) void this.enqueue(() => this.pushLocal(local));
      return { data: local?.data ?? null };
    }
    if (!local) return { data: await this.pull(kind, slot, server) };
    if (local.sha256 === server.sha256) {
      if (local.baseVersion !== server.headVersion || local.dirty) await putLocal({ ...local, baseVersion: server.headVersion, dirty: false });
      return { data: local.data };
    }
    if (!local.dirty) return { data: await this.pull(kind, slot, server) };
    if (local.baseVersion === server.headVersion) {
      void this.enqueue(() => this.pushLocal(local));
      return { data: local.data };
    }
    return { data: null, conflict: { local, server } };
  }

  private async pull(kind: SaveKind, slot: number, server: SaveSlot) {
    const r = await download(server.id);
    const prev = await getLocal(this.key(kind, slot));
    if (prev?.dirty) await backup(prev);
    await putLocal({
      key: this.key(kind, slot), userId: this.userId, gameId: this.gameId, kind, slot, engine: this.engine, core: this.core,
      coreVersion: server.coreVersion, data: r.data, sha256: r.sha256, baseVersion: r.version, dirty: false, updatedAt: Date.now(),
    });
    return r.data;
  }

  /** User picked a side in a conflict. Nothing is lost: the other copy stays in history/backups. */
  async resolveConflict(kind: SaveKind, slot: number, keep: 'local' | 'server'): Promise<Uint8Array | null> {
    const local = await getLocal(this.key(kind, slot));
    const server = this.server(kind, slot);
    if (!local) return server ? this.pull(kind, slot, server) : null;
    const r = await this.enqueue(() => resolveLocalConflict(local.key, keep, server?.headVersion));
    if (keep === 'local') return local.data;
    return r?.data ?? (server ? this.pull(kind, slot, server) : null);
  }

  /** New save data from the emulator: persist locally first, then sync. */
  async save(kind: SaveKind, slot: number, data: Uint8Array, screenshot?: Blob | null) {
    this.set({ state: 'saving' });
    const sha = await sha256Hex(data);
    const key = this.key(kind, slot);
    const prev = await getLocal(key);
    if (prev && prev.sha256 === sha && !prev.dirty) {
      this.set({ state: 'synced', at: Date.now() });
      return;
    }
    const v: LocalSave = {
      key, userId: this.userId, gameId: this.gameId, kind, slot, engine: this.engine, core: this.core, coreVersion: this.coreVersion,
      data, sha256: sha, baseVersion: prev?.baseVersion ?? this.server(kind, slot)?.headVersion ?? 0, dirty: true, updatedAt: Date.now(), screenshot: screenshot ?? null,
      conflict: prev?.conflict ?? null, title: this.title,
    };
    await putLocal(v);
    await this.enqueue(() => this.pushLocal(v));
  }

  async loadLocal(kind: SaveKind, slot: number) {
    return (await getLocal(this.key(kind, slot)))?.data ?? null;
  }

  /** Latest copy for loading a state: cloud if newer, else local. */
  async latest(kind: SaveKind, slot: number) {
    await this.refresh().catch(() => false);
    const r = await this.resolve(kind, slot);
    return r.conflict ? r.conflict.local.data : r.data;
  }

  private enqueue<T>(fn: () => Promise<T>) {
    const p = this.chain.then(fn, fn);
    this.chain = p.catch(() => {});
    return p;
  }

  private async pushLocal(v: LocalSave, force = false) {
    // Reload: another save may have landed in between.
    const cur = (await getLocal(v.key)) ?? v;
    if (!cur.dirty && !force) return;
    try {
      const slot = await upload(cur, force);
      const latest = await getLocal(v.key);
      // Only clear dirty if nothing newer was written meanwhile.
      if (latest && latest.sha256 === cur.sha256) await putLocal({ ...latest, baseVersion: slot.headVersion, dirty: false });
      else if (latest) await putLocal({ ...latest, baseVersion: slot.headVersion });
      if (this.serverSlots) this.serverSlots = [...this.serverSlots.filter((s) => s.id !== slot.id), slot];
      this.set({ state: 'synced', at: Date.now() });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'save_conflict') {
        const server = (e.details as { server: SaveSlot | null })?.server;
        if (server) {
          this.serverSlots = [...(this.serverSlots ?? []).filter((s) => s.id !== server.id), server];
          await putLocal({ ...cur, conflict: { headVersion: server.headVersion, sha256: server.sha256, updatedAt: server.updatedAt, deviceName: server.deviceName } });
          this.set({ state: 'conflict', local: cur, server });
        }
      } else if (e instanceof ApiError && e.retryable) {
        this.set({ state: 'pending', reason: 'Guardado en este dispositivo. Se subirá a la nube al recuperar la conexión.' });
      } else {
        this.set({ state: 'error', message: e instanceof Error ? e.message : 'Error al sincronizar' });
      }
    }
  }

  async retryPending() {
    for (const v of await listLocal()) {
      if (v.dirty && v.userId === this.userId && v.gameId === this.gameId && this.status.state !== 'conflict') await this.enqueue(() => this.pushLocal(v));
    }
  }
}

export interface SyncReport {
  synced: number;
  conflicts: number;
  failed: number;
  pending: number;
}

let syncing: Promise<SyncReport> | null = null;

/**
 * Upload every save made offline (app start, reconnection, manual "Sincronizar").
 * Never overwrites the cloud silently: if the cloud moved on, the save is
 * marked as a conflict for the user to resolve. Safe to call repeatedly.
 */
export function syncAllPending(userId: string): Promise<SyncReport> {
  syncing ??= (async () => {
    const report: SyncReport = { synced: 0, conflicts: 0, failed: 0, pending: 0 };
    const all = await listLocal().catch(() => [] as LocalSave[]);
    for (const v of all.filter((x) => x.dirty && x.userId === userId)) {
      if (v.conflict) {
        report.conflicts++;
        continue;
      }
      try {
        const slot = await upload(v);
        const latest = await getLocal(v.key);
        if (latest && latest.sha256 === v.sha256) await putLocal({ ...latest, baseVersion: slot.headVersion, dirty: false, conflict: null });
        else if (latest) await putLocal({ ...latest, baseVersion: slot.headVersion });
        report.synced++;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'save_conflict') {
          const server = (e.details as { server: SaveSlot | null })?.server;
          await putLocal({ ...v, conflict: server ? { headVersion: server.headVersion, sha256: server.sha256, updatedAt: server.updatedAt, deviceName: server.deviceName } : null });
          report.conflicts++;
        } else {
          report.failed++;
          if (e instanceof ApiError && e.retryable) report.pending++;
        }
      }
    }
    window.dispatchEvent(new CustomEvent('replay:sync', { detail: report }));
    return report;
  })().finally(() => (syncing = null));
  return syncing;
}

/** Saves on this device that are not in the cloud yet (dirty), and those in conflict. */
export async function pendingSaves(userId: string) {
  const all = await listLocal().catch(() => [] as LocalSave[]);
  return all.filter((x) => x.userId === userId && x.dirty);
}

/**
 * Resolve a conflict keeping BOTH versions in the cloud history:
 * - keep 'local': this device's save becomes the current version (the cloud's
 *   previous version stays in history).
 * - keep 'server': this device's save is uploaded first (so it is preserved in
 *   history), then the cloud version is restored as the current one and
 *   downloaded here.
 */
export async function resolveLocalConflict(key: string, keep: 'local' | 'server', serverHead?: number) {
  const local = await getLocal(key);
  if (!local) return null;
  const head = serverHead ?? local.conflict?.headVersion;
  const uploaded = await upload(local, true);
  if (keep === 'local') {
    await putLocal({ ...local, baseVersion: uploaded.headVersion, dirty: false, conflict: null });
    return { data: local.data };
  }
  if (!head) throw new Error('No se conoce la versión de la nube');
  await backup(local);
  const restored = await api<SaveSlot>(`/api/saves/${uploaded.id}/restore`, { body: { version: head, device: deviceName() } });
  const r = await download(restored.id);
  await putLocal({ ...local, data: r.data, sha256: r.sha256, baseVersion: r.version, dirty: false, conflict: null, updatedAt: Date.now() });
  window.dispatchEvent(new CustomEvent('replay:sync', { detail: null }));
  return { data: r.data };
}
